import { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import { v4 as uuid } from "uuid";
import { workspaces as workspacesApi } from "apis/callables";
import { emailRegex } from "common/regex";
import * as StripeUtils from "common/utils/stripeUtils";
import { auth } from "js/firebase/auth";
import BillingController from "js/controllers/BillingController";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import getLogger, { LogGroup } from "js/core/logger";

import type { IStripeSubscriptionExtended, IWorkspaceUserInvite } from "common/interfaces";
import { WorkspaceUserRole } from "common/interfaces";
import { t } from "js/i18n";

const logger = getLogger(LogGroup.BILLING);

const DATE_FORMAT = "MMM DD, YYYY";

export type InviteEmail = { id: string; email: string };

export interface UseInviteMembersWithBillingParams {
    workspaceId: string;
    availableSeatCount: number;
    paidSeatCount: number;
    existingUserEmails: string[];
    subscription?: IStripeSubscriptionExtended | null;
}

export type InviteUsersFn = (
    invites: Pick<IWorkspaceUserInvite, "email" | "role">[],
    analyticsProps: { quantity_new_seats_added: number }
) => Promise<unknown>;

export interface ExecuteInviteParams {
    onSuccess: () => void;
    inviteUsers: InviteUsersFn;
    /** Optional progress title when not adding seats (default: "Inviting members...") */
    progressTitle?: string;
}

export interface UseInviteMembersWithBillingResult {
    members: InviteEmail[];
    handleEmailsChange: (emails: string[]) => Promise<void>;
    immediateTotal: number;
    nextInvoiceTotal: number;
    computedNextBillDate: string;
    isCalculatingTotals: boolean;
    hasError: boolean;
    setHasError: (hasError: boolean) => void;
    filteredMembers: InviteEmail[];
    membersAreValid: boolean;
    additionalSeatCount: number;
    isInvitingSelf: boolean;
    executeInvite: (params: ExecuteInviteParams) => Promise<void>;
}

export function useInviteMembersWithBilling({
    workspaceId,
    availableSeatCount,
    paidSeatCount,
    existingUserEmails,
    subscription,
}: UseInviteMembersWithBillingParams): UseInviteMembersWithBillingResult {
    const [members, setMembers] = useState<InviteEmail[]>([]);
    const [immediateTotal, setImmediateTotal] = useState<number>(0);
    const [nextInvoiceTotal, setNextInvoiceTotal] = useState<number>(0);
    const [computedNextBillDate, setComputedNextBillDate] = useState<string>(() =>
        subscription
            ? moment.unix(subscription.current_period_end).format(DATE_FORMAT)
            : moment().add(1, "month").format(DATE_FORMAT)
    );
    const [isCalculatingTotals, setIsCalculatingTotals] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);
    const lastRequestIdRef = useRef<number>(0);

    const filteredMembers = members.filter(m => m.email);
    const membersAreValid =
        filteredMembers.length > 0 &&
        !filteredMembers.some(member => member.email.length > 0 && !emailRegex.test(member.email)) &&
        !filteredMembers.some(member =>
            filteredMembers.some(
                otherMember =>
                    member.email &&
                    member.email.toLowerCase() === otherMember.email.toLowerCase() &&
                    member.id !== otherMember.id
            )
        ) &&
        !filteredMembers.some(member => existingUserEmails.includes(member.email.toLowerCase()));

    const additionalSeatCountRaw = filteredMembers.length - availableSeatCount;
    const additionalSeatCount = additionalSeatCountRaw > 0 ? additionalSeatCountRaw : 0;

    const currentUserEmail = auth().currentUser?.email?.toLowerCase();
    const isInvitingSelf =
        !!currentUserEmail && filteredMembers.some(m => m.email?.toLowerCase() === currentUserEmail);

    useEffect(() => {
        if (subscription) {
            setComputedNextBillDate(moment.unix(subscription.current_period_end).format(DATE_FORMAT));
        }
    }, [subscription?.current_period_end]);

    const handleEmailsChange = async (emails: string[]) => {
        const nextMembers = emails.filter(Boolean).map(email => ({ id: uuid(), email }));
        setMembers(nextMembers);

        const nextAdditionalSeatCountRaw = nextMembers.length - availableSeatCount;
        const nextAdditionalSeatCount = nextAdditionalSeatCountRaw > 0 ? nextAdditionalSeatCountRaw : 0;

        if (nextAdditionalSeatCount > 0) {
            setIsCalculatingTotals(true);
            const requestId = ++lastRequestIdRef.current;
            const totalQuantity = paidSeatCount + nextAdditionalSeatCount;
            const trialDaysRemaining = subscription ? StripeUtils.Subscription.getTrialDaysRemaining(subscription) : 0;

            try {
                const { immediate, upcoming } = await workspacesApi.previewInvoicesForQuantity({
                    workspaceId,
                    quantity: totalQuantity,
                });

                if (requestId === lastRequestIdRef.current) {
                    setImmediateTotal((immediate?.total ?? 0) / 100);
                    if (upcoming) {
                        setNextInvoiceTotal((upcoming?.total ?? 0) / 100);
                        if (trialDaysRemaining > 0) {
                            setComputedNextBillDate(moment().add(trialDaysRemaining, "days").format(DATE_FORMAT));
                        } else {
                            setComputedNextBillDate(moment(upcoming?.period_end * 1000).format(DATE_FORMAT));
                        }
                    } else {
                        setNextInvoiceTotal(0);
                        setComputedNextBillDate(
                            subscription
                                ? moment.unix(subscription.current_period_end).format(DATE_FORMAT)
                                : moment().add(1, "month").format(DATE_FORMAT)
                        );
                    }
                }
            } catch {
                if (requestId === lastRequestIdRef.current) {
                    setImmediateTotal(0);
                    setNextInvoiceTotal(0);
                    setComputedNextBillDate(
                        subscription
                            ? moment.unix(subscription.current_period_end).format(DATE_FORMAT)
                            : moment().add(1, "month").format(DATE_FORMAT)
                    );
                }
            } finally {
                if (requestId === lastRequestIdRef.current) {
                    setIsCalculatingTotals(false);
                }
            }
        } else {
            setImmediateTotal(0);
            setNextInvoiceTotal(0);
            setComputedNextBillDate(
                subscription
                    ? moment.unix(subscription.current_period_end).format(DATE_FORMAT)
                    : moment().add(1, "month").format(DATE_FORMAT)
            );
            setIsCalculatingTotals(false);
        }
    };

    const executeInvite = useCallback(
        async ({ onSuccess, inviteUsers, progressTitle }: ExecuteInviteParams) => {
            await withProgressDialog({
                title:
                    additionalSeatCount > 0
                        ? t("Adding seats and inviting members...")
                        : progressTitle ?? t("Inviting members..."),
                action: async setProgress => {
                    try {
                        if (additionalSeatCount > 0) {
                            setProgress(0.3);
                            await BillingController.updateSubscriptionQuantity(paidSeatCount + additionalSeatCount);
                            setProgress(0.6);
                        }

                        const invites: Pick<IWorkspaceUserInvite, "email" | "role">[] = filteredMembers
                            .filter(m => m.email?.toLowerCase() !== currentUserEmail)
                            .map(m => ({ email: m.email, role: WorkspaceUserRole.MEMBER }));

                        if (invites.length > 0) {
                            setProgress(0.8);
                            await inviteUsers(invites, { quantity_new_seats_added: additionalSeatCount });
                        }
                        setProgress(1);

                        onSuccess();
                    } catch (err) {
                        logger.error(err, "[useInviteMembersWithBilling] Failed to invite members");

                        ShowErrorDialog({
                            title: t("Failed to invite members"),
                            message: err.message
                        });
                        throw err;
                    }
                }
            });
        },
        [additionalSeatCount, paidSeatCount, filteredMembers, currentUserEmail, t]
    );

    return {
        members,
        handleEmailsChange,
        immediateTotal,
        nextInvoiceTotal,
        computedNextBillDate,
        isCalculatingTotals,
        hasError,
        setHasError,
        filteredMembers,
        membersAreValid,
        additionalSeatCount,
        isInvitingSelf,
        executeInvite,
    };
}
